import {
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { BaseComponent } from '../base/base.component';
import { ButtonDirective } from "../button/button/button.directive";
import { MatRippleModule } from '@angular/material/core';

/**
 * A single entry in the notification panel.
 */
export interface NotificationItem {
  id: string;

  /** Headline, e.g. "Neuer Beitrag: Rauchwarnmelder: jährliche Prüfung im August". */
  title: string;

  /** Property / address the post belongs to, e.g. "Osterfeldstraße 89". */
  address: string;

  /** Channel the post was published in, e.g. "Gemeinschaft" or "Vorderhaus". */
  channel: string;

  /** Author of the post, e.g. "Julia Ahrens". */
  author: string;

  /** Whether the notification has already been read. */
  read: boolean;
}

@Component({
  selector: 'immo-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.css'],
  imports: [OverlayModule, A11yModule, MatIconModule, ButtonDirective, MatRippleModule],
})
export class NotificationPanelComponent extends BaseComponent {
  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Whether the panel is currently open. */
  readonly opened: WritableSignal<boolean> = signal(false);

  // -----------------------------------------------------------------------------------------------------
  // @ INPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** The notifications to display, newest first. */
  readonly notifications = input.required<NotificationItem[]>();

  // -----------------------------------------------------------------------------------------------------
  // @ OUTPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Emits the notification the user activated. */
  readonly notificationSelected = output<NotificationItem>();

  /** Emits when the user marks all notifications as read. */
  readonly markAllRead = output<void>();

  /** Emits when the user requests the full notification list. */
  readonly viewAllRequested = output<void>();

  // -----------------------------------------------------------------------------------------------------
  // @ VIEW CHILDREN
  // -----------------------------------------------------------------------------------------------------

  private readonly triggerElement =
    viewChild.required<ElementRef<HTMLButtonElement>>('triggerElement');

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Number of unread notifications, shown in the badge on the bell. */
  readonly unreadCount = computed(() => {
    return this.notifications().filter((notification) => !notification.read).length;
  });

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggles the panel open or closed.
   *
   * @returns {void}
   */
  toggle(): void {
    if (this.opened()) {
      this.close();

      return;
    }

    this.opened.set(true);
  }

  /**
   * Closes the panel and returns focus to the bell trigger.
   *
   * @returns {void}
   */
  close(): void {
    if (!this.opened()) {
      return;
    }

    this.opened.set(false);
    this.triggerElement().nativeElement.focus();
  }

  /**
   * Closes the panel when the user presses Escape inside the overlay.
   *
   * @param event - The keyboard event forwarded by the overlay.
   * @returns {void}
   */
  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Emits the activated notification and closes the panel so the
   * consumer can navigate to the underlying post.
   *
   * @param notification - The notification the user activated.
   * @returns {void}
   */
  onNotificationSelected(notification: NotificationItem): void {
    if (this.IsNullOrUndefined(notification)) {
      return;
    }

    this.notificationSelected.emit(notification);
    this.close();
  }

  /**
   * Requests that all notifications be marked as read. The panel stays
   * open so the user can still browse the entries.
   *
   * @returns {void}
   */
  onMarkAllRead(): void {
    this.markAllRead.emit();
  }

  /**
   * Requests navigation to the full notification list and closes the panel.
   *
   * @returns {void}
   */
  onViewAll(): void {
    this.viewAllRequested.emit();
    this.close();
  }
}
